import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import { useToast } from '../../context/ToastContext';
import { getCompMatcherCandidates, updateStudentCompMatcher } from '../../services/pocOwnerService';
import styles from './CompetitionMatcherAccess.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

/**
 * Competition matcher access, per student.
 *
 * Enabling is two settings at once — the access and the project the matcher
 * runs against — because granting one without the other gives the student a
 * tab that opens onto nothing. The project dropdown is therefore part of the
 * row, and Enable stays disabled until one is chosen.
 *
 * Only students on this POC owner's own projects appear here; the project list
 * is what they assign from, so anyone else would have nothing actionable.
 */
const CompetitionMatcherAccess = () => {
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [busyStudentId, setBusyStudentId] = useState(null);
  // Project chosen in the dropdown but not yet saved, per student id.
  const [selectedProject, setSelectedProject] = useState({});

  const fetchStudents = useCallback(async () => {
    try {
      const data = await getCompMatcherCandidates();
      setStudents(data);
      // Seed each dropdown from what is already saved, so a student who is
      // already enabled shows their real project rather than a blank select.
      setSelectedProject(
        Object.fromEntries(
          data
            .filter((s) => s.comp_matcher_project_id)
            .map((s) => [s.id, s.comp_matcher_project_id])
        )
      );
    } catch (error) {
      toast.error('Error loading students: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleToggle = async (student) => {
    const enabling = !student.comp_matcher_enabled;
    const projectId = selectedProject[student.id] || null;

    if (enabling && !projectId) {
      toast.error('Choose the project the matcher should run against first.');
      return;
    }

    setBusyStudentId(student.id);
    try {
      await updateStudentCompMatcher(student.id, enabling, projectId);
      toast.success(
        `Competition matcher ${enabling ? 'enabled' : 'disabled'} for ` +
          `${student.first_name} ${student.last_name}`
      );
      fetchStudents();
    } catch (error) {
      toast.error('Error updating competition matcher access: ' + error.message);
    } finally {
      setBusyStudentId(null);
    }
  };

  const columns = [
    {
      header: 'Student',
      accessor: 'first_name',
      render: (row) => (
        <div>
          <div className={styles.studentName}>
            {row.first_name} {row.last_name}
          </div>
          <div className={styles.studentMeta}>{row.email}</div>
        </div>
      ),
    },
    {
      header: 'School',
      accessor: 'school',
      render: (row) => row.school || '—',
    },
    {
      header: 'Project for matching',
      sortable: false,
      render: (row) => (
        <select
          className={styles.projectSelect}
          value={selectedProject[row.id] || ''}
          // Changing the project of an already-enabled student is a re-grant,
          // not a silent edit: they have to Disable and Enable again, so the
          // change is deliberate and lands in the audit log the same way.
          disabled={row.comp_matcher_enabled || busyStudentId === row.id}
          onChange={(e) =>
            setSelectedProject((prev) => ({ ...prev, [row.id]: e.target.value }))
          }
        >
          <option value="">Select a project…</option>
          {sortProjectsByName((row.projects || [])).map((project) => (
            <option key={project.id} value={project.id}>
              {project.name || 'Untitled project'}
            </option>
          ))}
        </select>
      ),
    },
    {
      header: 'Matcher Access',
      sortable: false,
      render: (row) => (
        <div className={styles.accessCell}>
          <span
            className={row.comp_matcher_enabled ? styles.enabled : styles.disabled}
          >
            {row.comp_matcher_enabled ? 'Enabled' : 'Disabled'}
          </span>
          <Button
            size="small"
            variant="outline"
            onClick={() => handleToggle(row)}
            loading={busyStudentId === row.id}
          >
            {row.comp_matcher_enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      ),
    },
  ];

  const filtered = students.filter((student) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(term) ||
      (student.email || '').toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading students..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Competition Matcher</h1>
        <p className={styles.subtitle}>
          Students you enable here get a “Find Competitions” tab, matched against
          the project you choose.
        </p>
      </div>

      <Card>
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name or email..."
        />
        {filtered.length === 0 ? (
          <p className={styles.empty}>
            {students.length === 0
              ? 'No students on your projects yet.'
              : 'No students match that search.'}
          </p>
        ) : (
          <Table columns={columns} data={filtered} />
        )}
      </Card>
    </div>
  );
};

export default CompetitionMatcherAccess;
